import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  HeroSection,
  HeroBackground,
  HeroContent,
  HeroTitle,
  HeroText,
  ServicesGrid,
  ServiceCard,
  ServiceIcon,
  ServiceTitle,
  ServiceDescription,
  StatsSection,
  StatItem,
  StatNumber,
  StatLabel,
  ProjectsContainer,
  MainImageContainer,
  MainImage,
  ThumbnailsContainer,
  Thumbnail,
  ProjectTitle,
  ProjectDescription,
  ProjectKeyPoints,
  AboutSection,
  AboutImageContainer,
  AboutImage,
  AboutContent,
  AboutSubheading,
  AboutText,
  SectionTitle,
  SectionSubtitle,
  FeaturedProjectLayout,
  ProjectImageSection,
  ProjectDetailsSection,
  TestimonialsContainer,
  Testimonial,
  TestimonialText,
  TestimonialAuthor,
  TestimonialRole,
  LogoSection,
  LogosSectionTitle,
  LogoSliderContainer,
  LogoSliderTrack,
  LogoItem,
  LogoImage,
  ClientTypesContainer,
  ClientTypeCard,
  ClientTypeImage,
  ClientTypeOverlay,
  ClientTypeTitle,
  PressMediaContainer,
  // Nouveaux composants pour la galerie
  MediaGallery,
  GalleryItem,
  GalleryImage,
  GalleryCaption,
  // Composants pour la section Détails
  DetailsContainer,
  DetailsIntro,
  DetailsList,
  DetailsItem,
  DetailsText
} from '../styles/HomePage.styles';

import Button from '../components/Button';
import SectionContainer from '../components/SectionContainer';
import TestimonialsSection from '../components/home/TestimonialsSection';
import { getFeaturedProjects } from '../services/projectService'; 
import { getPublishedCatalogItems } from '../services/catalogService';
import { Project } from '../types'; 
import { CatalogItem } from '../types'; 
import { Link } from 'react-router-dom';

// Helper to pluralize category names consistently with GalleryPage
const pluralizeCategory = (cat: string): string => {
  const lower = cat.toLowerCase();
  if (lower === 'bureau' || lower === 'bureaux') return 'Bureaux';
  if (lower === 'restaurant' || lower === 'restaurants') return 'Restaurant';
  if (lower === 'résidentiel' || lower === 'residentiel' || lower === 'résidentiels') return 'Résidentiel';
  return cat.charAt(0).toUpperCase() + cat.slice(1);
};

const PressLogosContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-lg);
  flex-wrap: wrap;
  margin-top: var(--spacing-md);
`;

const PressLogo = styled.img`
  height: 175px;
  object-fit: cover;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const ButtonContainer = styled.div`
  text-align: center;
  margin-top: var(--spacing-md);
`;

const ViewMoreButton = styled(Link)`
  display: inline-block;
  padding: var(--spacing-sm) var(--spacing-lg);
  background-color: transparent;
  border: 1px solid var(--color-text-dark);
  color: var(--color-text-dark);
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-weight: 500;
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: var(--color-text-dark);
    color: white;
  }
`;

const HomePage: React.FC = () => {
  const [latestProjects, setLatestProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState<boolean>(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  const [detailsItems, setDetailsItems] = useState<CatalogItem[]>([]);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(true);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoadingProjects(true);
      setProjectsError(null);
      try {
        const projectsData = await getFeaturedProjects();
        console.log('HomePage: Données brutes des projets publiés:', projectsData); 
        // getAllProjects retourne directement Project[]
        // getAllProjects trie déjà par created_at descendant, donc les plus récents sont en premier.
        setLatestProjects(projectsData.slice(0, 9));
        console.log('HomePage: latestProjects après set:', projectsData.slice(0, 9));
      } catch (err: any) {
        console.error('HomePage: Erreur lors du chargement des projets:', err);
        setProjectsError(err.message || 'Erreur lors du chargement des projets.');
        setProjectsError(err.message || 'Erreur lors du chargement des projets.');
      }
      setLoadingProjects(false);
    };

    const fetchDetails = async () => {
      setLoadingDetails(true);
      setDetailsError(null);
      try {
        const items = await getPublishedCatalogItems();
        setDetailsItems(items.slice(0, 9));
      } catch (err: any) {
        console.error('HomePage: Erreur lors du chargement des details:', err);
        setDetailsError(err.message || 'Erreur lors du chargement des détails.');
      }
      setLoadingDetails(false);
    };

    fetchProjects();
    fetchDetails();
  }, []);
  // État pour gérer l'image active dans la section projets
  const [activeThumb, setActiveThumb] = useState(0);
  
  // Données des projets
  const projectImages = [
    'https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/projects/da82135e-fc84-43a2-b44b-8c6d9de337a6/neuilly-pasteur-1744204651899.jpg',
    'https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/projects/da82135e-fc84-43a2-b44b-8c6d9de337a6/neuilly-pasteur-1744204652429.jpg',
    'https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/projects/da82135e-fc84-43a2-b44b-8c6d9de337a6/neuilly-pasteur-1744204654766.jpg',
    'https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/projects/da82135e-fc84-43a2-b44b-8c6d9de337a6/neuilly-pasteur-1744204655164.jpg',
    'https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/projects/da82135e-fc84-43a2-b44b-8c6d9de337a6/neuilly-pasteur-1744204655490.jpg',
    'https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/projects/da82135e-fc84-43a2-b44b-8c6d9de337a6/neuilly-pasteur-1744204656040.jpg',
  ];

  // Données des services
  const services = [
    {
      title: 'ARCHITECTURE INTÉRIEURE',
      subtitle: 'Transformer',
      description: 'Conception et modifications des espaces pour optimiser la circulation et les besoins en s\'adaptant à chaque style de vie et de travail pour les particuliers et les entreprises.'
    },
    {
      title: 'DÉCORATION',
      subtitle: 'Donner un nouveau souffle',
      description: 'Sans changer la structure des espaces, nous sélectionnons le mobilier, les luminaires, les matériaux, et tous les éléments pour la mise en place d\'un nouvel univers.'
    },
    {
      title: 'SCÉNOGRAPHIE',
      subtitle: 'Créer une ambiance',
      description: 'Répondre à un brief précis pour décorer un lieu pour un événement, accueillir du public ou créer un décor de toutes pièces pour une conférence de presse, un lancement, plateau de télévision, plateau de podcast...'
    }
  ];

  // Données statistiques
  const stats = [
    { number: '150+', label: 'Réalisations' },
    { number: '20', label: 'Années d\'Expérience' },
    { number: '25.000 m2', label: 'renovés' }
  ];
  
  // Données des témoignages
  const testimonials = [
    {
      text: "Une tres belle experience avec Fabienne de Pirey qui a refait pour nous un chalet à Megève: un excellent mélange de modernité, de montagne, de confort, ...une très grande réussite dans laquelle nous aimons tous nous retrouver. Je recommande trés vivement !",
      author: "Ghislaine Colella",
      role: "Propriétaire, Paris 16ème"
    },
    {
      text: "Accompagnement d’une professionnelle de l’architecture et décoration d’intérieur Un sens du détail et de la couleur. Après 3 expériences de chantier de rénovation totale, je recommande chaleureusement Fabienne pour sa créativité, son excellent goût (qui sait évidemment s’adapter au client) et son énergie",
      author: "Catherine de la Fouchardière",
      role: "Propriétaire, Paris 9ème"
    }
  ];
  
  // Données des logos de marques
  const brandLogos = [
    'https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/logo%20client/androuet.jpg',
    'https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/logo%20client/fremantle.png',
    'https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/logo%20client/oreal.svg',
    'https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/logo%20client/logo4.jpeg',
    'https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/logo%20client/logo5.png',
    'https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/logo%20client/logo6.png',
    'https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/logo%20client/logo-Treca-noir-new.png',
    'https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/logo%20client/images%20(2).png',
  ];
  
  return (
    <>
      <HeroSection>
        <HeroBackground />
        <HeroContent>
          <div className="hero-content-wrapper">
            <div className="hero-text-container">
              <HeroTitle>Architecture <br /><span style={{ color: '#C13221' }}>ET</span><br />Décoration d'intérieur</HeroTitle>
              <HeroText>
                Nous créons pour nos clients des espaces qui allient Fonctionnalité, Esthétisme
                et Harmonie. 
                <br /><br />
                Nous voulons être à la hauteur des exigences de nos clients et des souhaits de
                chacun.
                <br /><br />
                Pour nous, l'écoute et l'échange sont une priorité tant avec nos clients qu'avec nos équipes.
              </HeroText>
              <div style={{ marginTop: '20px', fontStyle: 'italic', fontSize: '1.1rem', fontWeight: 'bold', color: '#C13221' }}>
              À l'écoute des lieux, à l'écoute des vies
              </div>

            </div>
            <div className="hero-image-container">
              <img src="/images/hero-images.jpg" alt="Design d'intérieur minimaliste" className="hero-image" />
            </div>
          </div>
        </HeroContent>
      </HeroSection>

      {/* Nos services */}
      <SectionContainer spacing="small">
        <SectionTitle>LES <span style={{ color: '#C13221' }}>3</span> PILIERS DE L'AGENCE INTERIEURS</SectionTitle>
        <ServicesGrid>
          <ServiceCard>
            
            <ServiceTitle>{services[0].title}</ServiceTitle>
            <h4 style={{ fontStyle: 'italic', marginBottom: 'var(--spacing-xs)', opacity: '0.8' }}>{services[0].subtitle}</h4>
            <ServiceDescription>{services[0].description}</ServiceDescription>
            
          </ServiceCard>
          <ServiceCard>
            
            <ServiceTitle>{services[1].title}</ServiceTitle>
            <h4 style={{ fontStyle: 'italic', marginBottom: 'var(--spacing-xs)', opacity: '0.8' }}>{services[1].subtitle}</h4>
            <ServiceDescription>{services[1].description}</ServiceDescription>
            
          </ServiceCard>
          <ServiceCard>
            
            <ServiceTitle>{services[2].title}</ServiceTitle>
            <h4 style={{ fontStyle: 'italic', marginBottom: 'var(--spacing-xs)', opacity: '0.8' }}>{services[2].subtitle}</h4>
            <ServiceDescription>{services[2].description}</ServiceDescription>
            
          </ServiceCard>
        </ServicesGrid>
          <div style={{ marginTop: 'var(--spacing-md)', textAlign: 'center' }}>
            <Button to="#expertises" variant="outline">En savoir plus</Button>
          </div>
      </SectionContainer>

       {/* Types de clients */}
       <SectionContainer id="expertises" spacing="small">
        <SectionTitle>NOS <span style={{ color: '#C13221' }}>E</span>XPERTISES SPÉCIFIQUES</SectionTitle>
        <SectionSubtitle>Nous pouvons vous accompagner sur plusieurs points</SectionSubtitle>
        <ClientTypesContainer>
          {[
            {
              image: "/images/particuliers.jpeg",
              title: "RÉSIDENTIEL",
            },
            {
              image: "/images/Bureau.jpg",
              title: "AMÉNAGEMENT DE SIÈGES SOCIAUX ET DE BUREAUX",
            },
            {
              image: "/images/restaurant.jpg",
              title: "RESTAURANTS",
            },
            {
              image: "/images/hopital.jpg",
              title: "SERVICES HOSPITALIERS",
            },
            {
              image: "https://supabase.hkcom.fr/storage/v1/object/public/agence-interieurs/Service/plateaux-tv.jpeg",
              title: "SCÉNOGRAPHIE ET PLATEAUX TELEVISION",
            },
          ].map((clientType, index) => (
            <ClientTypeCard key={index}>
              <ClientTypeImage src={clientType.image} alt={clientType.title} />
              <ClientTypeOverlay>
                <ClientTypeTitle>{clientType.title}</ClientTypeTitle>
              </ClientTypeOverlay>
            </ClientTypeCard>
          ))}
        </ClientTypesContainer>

       </SectionContainer>
              {/* Arguments de confiance (Statistiques) */}
      <SectionContainer spacing="small" noDivider>
        <StatsSection>
          {stats.map((stat, index) => (
            <StatItem key={index}>
              <StatNumber>{stat.number}</StatNumber>
              <StatLabel>{stat.label}</StatLabel>
            </StatItem>
          ))}
        </StatsSection>
      </SectionContainer>
      
 {/* Logos de marques */}
 <SectionContainer spacing="small">
        <LogoSection>
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-md)', fontSize: '1.1rem', fontStyle: 'italic', opacity: '0.8' }}>
            A Paris, Londres, Ile de Ré, Megève, Nice, Carnac, Saint-Rémy-de-Provence......
          </div>
          <LogosSectionTitle>Ils nous font confiance</LogosSectionTitle>
          <LogoSliderContainer>
            <LogoSliderTrack>
              {/* Premier ensemble de logos */}
              {brandLogos.map((logo, index) => (
                <LogoItem key={`first-${index}`}>
                  <LogoImage src={logo} alt={`Marque partenaire ${index + 1}`} />
                </LogoItem>
              ))}
              {/* Deuxième ensemble de logos (pour créer un effet de boucle infinie) */}
              {brandLogos.map((logo, index) => (
                <LogoItem key={`second-${index}`}>
                  <LogoImage src={logo} alt={`Marque partenaire ${index + 1}`} />
                </LogoItem>
              ))}
            </LogoSliderTrack>
          </LogoSliderContainer>
        </LogoSection>
      </SectionContainer>


       {/* Galerie d'images */}
       <SectionContainer spacing="small">
         <SectionTitle><span style={{ color: '#C13221' }}>G</span>alerie</SectionTitle>
         <SectionSubtitle>Découvrez nos réalisations et projets en images</SectionSubtitle>
         <PressMediaContainer>
           <MediaGallery>
             {loadingProjects && <p>Chargement des projets...</p>}
             {projectsError && <p style={{ color: 'red' }}>{projectsError}</p>}
             {!loadingProjects && !projectsError && latestProjects.length === 0 && (
               <p>Aucun projet à afficher pour le moment.</p>
             )}
             {!loadingProjects && !projectsError && latestProjects.map((project: any) => (
               <GalleryItem key={project.id}>
                 <Link to={`/galerie/${project.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                   <GalleryImage>
                     {project.mainImage ? (
                       <img src={project.mainImage} alt={project.title} />
                     ) : (
                       <div style={{ width: '100%', height: '200px', backgroundColor: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Image non disponible</div>
                     )}
                   </GalleryImage>
                   <GalleryCaption>
                      <small style={{ textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.75rem', opacity: 0.7 }}>
                        {pluralizeCategory(project.category || '')}
                      </small>
                     <h3>{project.title}</h3>
                     
                   </GalleryCaption>
                 </Link>
               </GalleryItem>
             ))}
           </MediaGallery>
         </PressMediaContainer>
         <div style={{ marginTop: 'var(--spacing-md)', textAlign: 'center' }}>
           <Button to="/galerie" variant="outline">Toute la galerie</Button>
         </div>
       </SectionContainer>



      {/* Les Détails */}
      <SectionContainer background="light-gray" spacing="small">
        <SectionTitle>LES <span style={{ color: '#C13221' }}>D</span>ÉTAILS</SectionTitle>
        <DetailsContainer>
          {/* Texte introductif et points clés */}
          <DetailsIntro>
            Nous accordons beaucoup d’importance aux détails.
          </DetailsIntro>
          <ul style={{ listStyleType: 'disc', marginLeft: '1.5rem', lineHeight: 1.4 }}>
            <li>Détails pour les finitions du travail de nos artisans</li>
            <li>Détails pour le choix des matériaux</li>
            <li>Détails du mobilier proposé</li>
          </ul>
          <DetailsIntro>
            Rien n’est laissé au hasard. Tout est étudié.
          </DetailsIntro>
          {loadingDetails && <p>Chargement des détails...</p>}
          {detailsError && <p style={{ color: 'red' }}>{detailsError}</p>}
          {!loadingDetails && !detailsError && (
            <DetailsList>
              {detailsItems.map((item) => (
                <DetailsItem key={item.id}>
                  {item.image ? (
                    <img src={item.image} alt={item.title || 'detail'} style={{ width: '100%', height: 'auto' }} />
                  ) : (
                    <DetailsText>Aucune image</DetailsText>
                  )}
                  {item.title && <DetailsText style={{ marginTop: '0.5rem' }}>{item.title}</DetailsText>}
                </DetailsItem>
              ))}
            </DetailsList>
          )}
          <div style={{ marginTop: 'var(--spacing-md)', textAlign: 'center' }}>
            <Button to="/details" variant="outline">Découvrir plus de détails</Button>
          </div>
        </DetailsContainer>
      </SectionContainer>

            {/* Retombées Presse */}
            <SectionContainer spacing="small">
        <SectionTitle><span style={{ color: '#C13221' }}>R</span>etombées Presse</SectionTitle>
        <SectionSubtitle>Ils parlent de nous</SectionSubtitle>
        <PressLogosContainer>
          <PressLogo src="/images/presse/Agence_Interieurs_Presse-001.jpg" alt="Retombée Presse 1" />
          <PressLogo src="/images/presse/Agence_Interieurs_Presse-002.jpg" alt="Retombée Presse 2" />
          <PressLogo src="/images/presse/Agence_Interieurs_Presse-003.jpg" alt="Retombée Presse 3" />
        </PressLogosContainer>
      </SectionContainer>
      
      {/* Avis clients */}
      <SectionContainer spacing="small">
        <SectionTitle><span style={{ color: '#C13221' }}>T</span>émoignages</SectionTitle>
        <SectionSubtitle>Ce que nos clients pensent de nous</SectionSubtitle>
        <TestimonialsContainer>
          {testimonials.map((testimonial, index) => (
            <Testimonial key={index}>
              <TestimonialText>
                {testimonial.text}
              </TestimonialText>
              <div>
                <TestimonialAuthor>{testimonial.author}</TestimonialAuthor>
                <TestimonialRole>{testimonial.role}</TestimonialRole>
              </div>
            </Testimonial>
          ))}
        </TestimonialsContainer>
      </SectionContainer>


      {/* À propos de nous */}
      <SectionContainer spacing="small">
        <SectionTitle><span style={{ color: '#C13221' }}>À</span> propos de nous</SectionTitle>
        <AboutSection>
          <AboutImageContainer>
            <AboutImage src="/images/about.png" alt="Notre équipe" />
          </AboutImageContainer>
          <AboutContent>
            
            <AboutText>
              Fondée en 2005, l'Agence Intérieurs a été créée pour répondre aux exigences des particuliers
              et des entreprises en matière de rénovation, agencement intérieur et
              transformation des espaces de vie et de travail.
            </AboutText>
            <AboutText>
              Nous sommes en recherche constante de nouveaux matériaux, de nouvelles
              technicités afin de réaliser des projets tous différents les uns des autres.
              Nous aménageons avec le même engouement des appartements, hotels
              particuliers, maisons, résidences secondaires mais aussi bureaux, sièges sociaux
              dans leur ensemble, des hôpitaux, des plateaux de television ou des lieux de
              réception…
            </AboutText>
            <AboutText>
              C'est une équipe compléte et soudée qui intervient sur chaque Projet, de la
              création de celui-ci à la mise en place finale.
            </AboutText>
            <Button to="/about" variant="outline">En savoir plus sur nous</Button>
          </AboutContent>
        </AboutSection>
      </SectionContainer>
    </>
  );
};

export default HomePage;
